// src/pages/RegisterPage.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  User,
  Eye,
  EyeOff,
  ChevronDown,
  Check,
  Building,
  Leaf,
  Lock,
  Phone,
} from "lucide-react";
import axios from "axios";
import { baseURL } from "../config";
import { toast } from "react-hot-toast";
import { useLoginContext } from "../Contexts/LoginContext";
import GoogleAuthentication from "../components/reusable/GoogleAuthentication";

const RegisterPage = () => {
  // Form state
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const {loggedIn,login}=useLoginContext();
  // UI state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Error states
  const [error, setError] = useState("");
  const [nameError, setNameError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  
  const navigate = useNavigate();

  useEffect(()=>{
    if(loggedIn)navigate("/");
  },[loggedIn]);

  // Validate name input (only letters and spaces)
  const validateName = (value) => {
    if (!value) return "Name is required";
    if (!/^[a-zA-Z\s]*$/.test(value)) return "Name can only contain letters and spaces";
    if (value.trim().length < 2) return "Name must be at least 2 characters";
    return "";
  };

// Validate mobile number (exactly 10 digits, realistic format)
const validateMobile = (value) => {
  if (!value) return "Mobile number is required";

  // Only 10 digits allowed
  if (!/^\d{10}$/.test(value)) {
    return "Mobile number must be 10 digits";
  }

  // Disallow numbers like 0000000000 or all same digits
  if (/^(\d)\1{9}$/.test(value)) {
    return "Invalid mobile number";
  }

  // Example: for India, must start with 6,7,8,9
  if (!/^[6-9]\d{9}$/.test(value)) {
    return "Invalid mobile number format";
  }

  return "";
};


  // Validate password
  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters";
    return "";
  };

  // Validate confirm password
  const validateConfirmPassword = (value) => {
    if (!value) return "Please confirm your password";
    if (value !== password) return "Passwords do not match";
    return "";
  };

  // Update error states on input change
  useEffect(() => {
    if (name) setNameError(validateName(name));
  }, [name]);

  useEffect(() => {
    if (mobile) setMobileError(validateMobile(mobile));
  }, [mobile]);

  useEffect(() => {
    if (password) setPasswordError(validatePassword(password));
  }, [password]);

  useEffect(() => {
    if (confirmPassword) setConfirmPasswordError(validateConfirmPassword(confirmPassword));
  }, [confirmPassword, password]);

  // Handle input changes with validation
  const handleNameChange = (e) => {
    const value = e.target.value;
    // Only allow letters and spaces
    if (/^[a-zA-Z\s]*$/.test(value)) {
      setName(value);
      setNameError(validateName(value));
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 10 characters
    if (/^\d{0,10}$/.test(value)) {
      setMobile(value);
      setMobileError(validateMobile(value));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(validateConfirmPassword(value));
  };

  // Function to clear error whenever any input field changes
  const handleChange = (setter) => (e) => {
    setter(e.target.value);
    setError(""); // Clear error message on input change
  };

  // Handles the registration process for email/password users
  const handleRegister = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Validate all fields before submission
    const nameErr = validateName(name);
    const mobileErr = validateMobile(mobile);
    const passwordErr = validatePassword(password);
    const confirmPasswordErr = validateConfirmPassword(confirmPassword);
    
    setNameError(nameErr);
    setMobileError(mobileErr);
    setPasswordError(passwordErr);
    setConfirmPasswordError(confirmPasswordErr);

    if (nameErr || mobileErr || passwordErr || confirmPasswordErr) {
      return;
    }

    // Validate if a role has been selected
    if (!selectedRole) {
      setShowRoleModal(true);
      setError("Please select a role");
      return;
    }

    // Validate if privacy policy and terms have been agreed to
    if (!agreed) {
      setShowPrivacyModal(true); // Show privacy modal if not agreed
      return;
    }

    setError(""); // Clear any previous errors
    setIsSubmitting(true); // Set submitting state to true

    try {
      // Make API call to register user
      const response = await api.post(`/register`, {
        name,
        username: mobile, // Assuming `mobile` is used as `username` for registration
        password,
        role: selectedRole,
      });

      const { token } = response.data;
      
      // Store the authentication token in local storage
      toast.success("Registration successful!"); // Show success notification
      login(token);
        } catch (error) {
      // Handle different types of errors from the API
      if (error.response?.status === 400) {
        setError(error.response.data.message || "Username already taken");
      } else {
        setError("Something went wrong. Please try again.");
      }
      console.error("Register error:", error);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };



  return (
    <div className="overflow-y-scroll min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex justify-center w-full mb-12 relative overflow-hidden font-inter">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      {/* Back Button */}
      <motion.button
        onClick={() => navigate("/")}
        className="z-50 fixed top-10 left-10 p-3 rounded-full bg-white bg-opacity-80 backdrop-blur-sm shadow-md hover:bg-opacity-100 transition-all"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <ArrowLeft className="h-5 w-5 text-indigo-600" />
      </motion.button>

      <div className="w-full max-w-4xl mx-auto py-8 px-4 relative z-10">


        {/* Main content area */}
        <div className="flex flex-col lg:flex-row gap-8 mt-10">
          {/* Email Registration Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-1 bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100"
          >
            <div className="p-8 md:p-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">
                Register with Mobile Number
              </h2>

              {/* Global error message display */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </motion.div>
              )}

              {/* Registration Form */}
              <form onSubmit={handleRegister} className="space-y-5">
                {/* Full Name Input */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={handleNameChange}
                      placeholder="John Doe"
                      required
                      className={`w-full px-4 py-3 pl-12 border ${
                        nameError ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-gray-800 placeholder-gray-400 shadow-sm`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  {nameError && (
                    <p className="mt-1 text-sm text-red-600">{nameError}</p>
                  )}
                </div>

                {/* Mobile Number Input */}
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <input
                      id="mobile"
                      type="tel"
                      value={mobile}
                      onChange={handleMobileChange}
                      placeholder="1234567890"
                      required
                      className={`w-full px-4 py-3 pl-12 border ${
                        mobileError ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-gray-800 placeholder-gray-400 shadow-sm`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  {mobileError && (
                    <p className="mt-1 text-sm text-red-600">{mobileError}</p>
                  )}
                </div>

                {/* Password Input */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="••••••••"
                      required
                      className={`w-full px-4 py-3 pl-12 pr-12 border ${
                        passwordError ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-gray-800 placeholder-gray-400 shadow-sm`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-indigo-600 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                  )}
                </div>

                {/* Confirm Password Input */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="••••••••"
                      required
                      className={`w-full px-4 py-3 pl-12 pr-12 border ${
                        confirmPasswordError ? 'border-red-500' : 'border-gray-300'
                      } rounded-xl focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-gray-800 placeholder-gray-400 shadow-sm`}
                    />
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-indigo-600 focus:outline-none"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {confirmPasswordError && (
                    <p className="mt-1 text-sm text-red-600">{confirmPasswordError}</p>
                  )}
                </div>

                {/* Role Selector Dropdown */}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Role
                  </label>
                  <div className="relative">
                    <select
                      id="role"
                      value={selectedRole || ""}
                      onChange={(e) => {
                        setSelectedRole(e.target.value || null);
                        setError("");
                      }}
                      required
                      className="w-full px-4 py-3 pl-4 pr-10 border border-gray-300 rounded-xl appearance-none focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-gray-800 shadow-sm"
                    >
                      <option value="" disabled>
                        -- Choose your role --
                      </option>
                      <option value="admin">Dairy Owner</option>
                      <option value="user">
                        Milk Supplier / Farmer
                      </option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </div>
                </div>

                {/* Privacy Policy Checkbox */}
                <div className="flex items-start gap-3 pt-2">
                  <input
                    id="agreeCheckbox"
                    type="checkbox"
                    checked={agreed}
                    onChange={() => {
                      setAgreed(!agreed);
                      setError("");
                    }}
                    className="mt-1 w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 cursor-pointer"
                  />
                  <label htmlFor="agreeCheckbox" className="text-sm text-gray-600 cursor-pointer">
                    I agree to the{" "}
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline font-medium"
                      onClick={() => setShowPrivacyModal(true)}
                    >
                      Privacy Policy
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline font-medium"
                      onClick={() => setShowPrivacyModal(true)}
                    >
                      Terms of Service
                    </button>
                  </label>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full mt-6 py-3 px-6 rounded-xl shadow-lg font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2
                    ${isSubmitting
                      ? "bg-indigo-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-98"
                    }`}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </motion.button>

                {/* Link to Login page */}
                <div className="text-center text-sm text-gray-600 mt-6">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:underline font-bold"
                  >
                    Login here
                  </Link>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Quick Google Registration Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex-1 bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col items-center justify-center p-8 md:p-10 text-center"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4 w-full">
              Google Registration
            </h2>
            <p className="text-gray-600 mb-8 max-w-sm">
              Register quickly and securely using your Google account.
            </p>

            {/* Button to trigger role selection modal for Google registration */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full max-w-xs py-3 px-6 bg-white border border-gray-300 rounded-xl shadow-md flex items-center justify-center gap-3 text-gray-700 font-semibold hover:bg-gray-50 transition-all duration-200"
            >
              <GoogleAuthentication/>
            </motion.button>

          </motion.div>
        </div>
      </div>

      {/* Role Selection Modal */}
      {showRoleModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100"
          >
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Select Your Role
              </h2>
              <p className="text-gray-600 mt-2">
                Please choose the role that best describes you to access the
                right features.
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Role Card for Dairy Owner */}
              <motion.div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ease-in-out
                  ${selectedRole === "admin"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 hover:border-indigo-400 hover:shadow-sm"
                  }`}
                whileHover={{ y: -5, scale: 1.01 }}
                onClick={() => {
                  setSelectedRole("admin");
                  setShowRoleModal(false);
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 rounded-lg text-indigo-600 flex-shrink-0">
                    <Building className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">Dairy Owner</h3>
                    <p className="text-gray-600 text-sm mt-1">I own or manage a dairy farm or cooperative.</p>
                  </div>
                </div>
              </motion.div>

              {/* Role Card for Milk Supplier / Farmer */}
              <motion.div
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ease-in-out
                  ${selectedRole === "user"
                    ? "border-indigo-500 bg-indigo-50 shadow-md"
                    : "border-gray-200 hover:border-indigo-400 hover:shadow-sm"
                  }`}
                whileHover={{ y: -5, scale: 1.01 }}
                onClick={() => {
                  setSelectedRole("user");
                  setShowRoleModal(false);
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 rounded-lg text-indigo-600 flex-shrink-0">
                    <Leaf className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">Milk Supplier / Farmer</h3>
                    <p className="text-gray-600 text-sm mt-1">I supply milk to dairy farms and want to track payments.</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                className="px-6 py-2 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Privacy Policy & Terms Modal */}
      {showPrivacyModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-100"
          >
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                Privacy Policy & Terms
              </h2>
            </div>

            {/* Scrollable content area for policy text */}
            <div className="flex-1 overflow-y-auto p-6 text-gray-700">
              <div className="prose prose-indigo max-w-none">
                <h3>Data Collection</h3>
                <p>
                  We collect information that you provide directly to us, such
                  as your name, email address, phone number, and other contact
                  or identifying information. We also collect information about
                  your dairy operations including milk collection data,
                  financial transactions, and farmer details.
                </p>

                <h3>How We Use Your Information</h3>
                <p>
                  We use the information we collect to provide, maintain, and
                  improve our services, including to process transactions and
                  send you technical notices and support messages. We may also
                  use the information to send you administrative messages and
                  other information related to your account.
                </p>

                <h3>Data Security</h3>
                <p>
                  We take reasonable measures to help protect information about
                  you from loss, theft, misuse and unauthorized access,
                  disclosure, alteration, and destruction. All data is encrypted
                  during transmission and at rest.
                </p>

                <h3>Your Choices</h3>
                <p>
                  You may update, correct, or delete information about you at
                  any time by logging into your online account. If you wish to
                  cancel your account, please contact us.
                </p>
              </div>
            </div>

            {/* Modal action buttons */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                className="px-6 py-2 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                onClick={() => setShowPrivacyModal(false)}
              >
                Close
              </button>
              <button
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md"
                onClick={() => {
                  setAgreed(true);
                  setShowPrivacyModal(false);
                }}
              >
                I Agree
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default RegisterPage;